// The issue dialogs (MEETINGS): raise or edit an issue (title, team or the company, owner seat,
// priority, and what it was raised from), solve one (to-dos with the 7-day default due, a
// decision, or just close it), and `RaiseIssueButton`, the visible "Raise issue" action other
// screens put on a chat message, a task, a missed KPI and an off-track priority.
import {
  Button,
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Field,
  FieldLabel,
  Input,
  NativeSelect,
  NativeSelectOption,
  Segmented,
  Textarea,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
  toast,
} from "@companyos/ui";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { CircleAlertIcon, PlusIcon, XIcon } from "lucide-react";
import { type FormEvent, type ReactNode, useEffect, useId, useState } from "react";
import type { Api } from "../../api/client";
import type { IssueView } from "../../api/types";
import { problemText } from "../settings/problem-text";
import { defaultDue, dueInstant, PRIORITY_LABELS } from "./cadence-model";
import { cadenceKeys, issuesQuery, type SeatOption, useSeatOptions } from "./cadence-queries";

export type IssueSource = {
  kind: "manual" | "message" | "task" | "metric" | "goal" | "meeting";
  id: string | null;
  label: string | null;
};

export type IssueDefaults = {
  title?: string;
  /** A team id, or null for the company's list. */
  team_id?: string | null;
  owner_seat_id?: string | null;
  priority?: number;
  source?: IssueSource;
};

const COMPANY = "company";

/** Raise a new issue (`defaults` prefill it), or edit `issue`. */
export function IssueDialog({
  api,
  co,
  open,
  onOpenChange,
  issue,
  defaults,
  onSaved,
}: {
  api: Api;
  co: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  issue?: IssueView | undefined;
  defaults?: IssueDefaults | undefined;
  onSaved?: ((issue: IssueView) => void) | undefined;
}) {
  const qc = useQueryClient();
  const board = useQuery({ ...issuesQuery(api, co), enabled: open });
  const seats = useSeatOptions(api, co);
  const id = useId();
  const raisable = (board.data?.teams ?? []).filter((t) => t.can_raise || issue?.team_id === t.id);
  const fallbackTeam =
    defaults?.team_id !== undefined ? (defaults.team_id ?? COMPANY) : (raisable[0]?.id ?? COMPANY);
  const [title, setTitle] = useState("");
  const [team, setTeam] = useState<string>(COMPANY);
  const [owner, setOwner] = useState("");
  const [priority, setPriority] = useState(2);
  const [body, setBody] = useState("");
  // Each opening starts from the issue (or the defaults).
  // biome-ignore lint/correctness/useExhaustiveDependencies: reset on open only
  useEffect(() => {
    if (!open) return;
    setTitle(issue?.title ?? defaults?.title ?? "");
    setTeam(issue ? (issue.team_id ?? COMPANY) : fallbackTeam);
    setOwner(issue?.owner_seat_id ?? defaults?.owner_seat_id ?? "");
    setPriority(issue?.priority ?? defaults?.priority ?? 2);
    setBody(issue?.body ?? "");
  }, [open, issue?.id]);
  // The team list arrives after the dialog opens: a new issue lands on the first raisable team.
  // biome-ignore lint/correctness/useExhaustiveDependencies: once the teams load
  useEffect(() => {
    if (open && !issue && defaults?.team_id === undefined && raisable[0] && team === COMPANY)
      setTeam(raisable[0].id);
  }, [board.data]);

  const save = useMutation({
    mutationFn: () => {
      const team_id = team === COMPANY ? null : team;
      const owner_seat_id = owner || null;
      if (issue)
        return api.client.issues.update({
          params: { co, id: issue.id },
          body: {
            title: title.trim(),
            body: body.trim() || null,
            owner_seat_id,
            priority,
            team_id,
          },
        });
      return api.client.issues.create({
        params: { co },
        body: {
          title: title.trim(),
          ...(body.trim() ? { body: body.trim() } : {}),
          team_id,
          owner_seat_id,
          priority,
          ...(defaults?.source
            ? {
                source_kind: defaults.source.kind,
                source_id: defaults.source.id,
                source_label: defaults.source.label,
              }
            : {}),
        },
      });
    },
    onSuccess: (saved) => {
      void qc.invalidateQueries({ queryKey: cadenceKeys.root(co) });
      if (!issue)
        toast.add({
          title: "Issue raised",
          description: `${saved.title} · ${saved.team_name ?? "Company"}`,
          type: "success",
        });
      onSaved?.(saved);
      onOpenChange(false);
    },
  });
  const submit = (e: FormEvent) => {
    e.preventDefault();
    if (title.trim()) save.mutate();
  };
  const canCompany = board.data?.can_raise_company || issue?.team_id === null;

  return (
    <Dialog open={open} onOpenChange={(o) => !save.isPending && onOpenChange(o)}>
      <DialogContent>
        <form
          onSubmit={submit}
          aria-label={issue ? "Edit issue" : "Raise issue"}
          className="grid gap-5"
        >
          <DialogHeader>
            <DialogTitle>{issue ? "Edit issue" : "Raise an issue"}</DialogTitle>
            <DialogDescription>
              {defaults?.source?.label
                ? `From ${defaults.source.label}.`
                : "Issues go on a team's list (or the company's) and are worked identify → discuss → solve, usually in the weekly meeting."}
            </DialogDescription>
          </DialogHeader>
          <Field>
            <FieldLabel htmlFor={`${id}-title`}>Issue</FieldLabel>
            <Input
              id={`${id}-title`}
              value={title}
              maxLength={500}
              required
              autoFocus
              onChange={(e) => setTitle(e.target.value)}
              placeholder="What is the problem?"
            />
          </Field>
          <div className="grid grid-cols-2 gap-3 max-sm:grid-cols-1">
            <Field>
              <FieldLabel htmlFor={`${id}-team`}>List</FieldLabel>
              <NativeSelect
                id={`${id}-team`}
                value={team}
                onChange={(e) => setTeam(e.target.value)}
                className="w-full"
              >
                {raisable.map((t) => (
                  <NativeSelectOption key={t.id} value={t.id}>
                    {t.name}
                  </NativeSelectOption>
                ))}
                {canCompany ? (
                  <NativeSelectOption value={COMPANY}>Company</NativeSelectOption>
                ) : null}
              </NativeSelect>
            </Field>
            <Field>
              <FieldLabel htmlFor={`${id}-priority`}>Priority</FieldLabel>
              <NativeSelect
                id={`${id}-priority`}
                value={String(priority)}
                onChange={(e) => setPriority(Number(e.target.value))}
                className="w-full"
              >
                {[1, 2, 3].map((p) => (
                  <NativeSelectOption key={p} value={String(p)}>
                    {PRIORITY_LABELS[p]}
                  </NativeSelectOption>
                ))}
              </NativeSelect>
            </Field>
          </div>
          <SeatSelect
            id={`${id}-owner`}
            label="Owner seat"
            seats={seats}
            value={owner}
            onChange={setOwner}
          />
          <Field>
            <FieldLabel htmlFor={`${id}-body`}>Details (optional)</FieldLabel>
            <Textarea
              id={`${id}-body`}
              value={body}
              rows={3}
              onChange={(e) => setBody(e.target.value)}
            />
          </Field>
          {save.isError ? (
            <p role="alert" className="text-sm text-danger-text">
              {problemText(save.error, "The issue could not be saved.")}
            </p>
          ) : null}
          <DialogFooter>
            <DialogClose render={<Button type="button" variant="outline" />}>Cancel</DialogClose>
            <Button type="submit" disabled={save.isPending || !title.trim()}>
              {issue ? "Save" : "Raise issue"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

export function SeatSelect({
  id,
  label,
  seats,
  value,
  onChange,
  none = "No owner",
}: {
  id: string;
  label: string;
  seats: readonly SeatOption[];
  value: string;
  onChange: (id: string) => void;
  none?: string;
}) {
  return (
    <Field>
      <FieldLabel htmlFor={id}>{label}</FieldLabel>
      <NativeSelect
        id={id}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="w-full"
      >
        <NativeSelectOption value="">{none}</NativeSelectOption>
        {seats.map((s) => (
          <NativeSelectOption key={s.id} value={s.id}>
            {s.label}
          </NativeSelectOption>
        ))}
      </NativeSelect>
    </Field>
  );
}

type TodoRow = { key: number; title: string; seat: string; due: string };

/** The to-do rows of a solve or a meeting outcome: title, assignee seat, due (+7 days). */
export function TodoRows({
  rows,
  seats,
  onChange,
}: {
  rows: TodoRow[];
  seats: readonly SeatOption[];
  onChange: (rows: TodoRow[]) => void;
}) {
  const id = useId();
  const put = (k: number, patch: Partial<TodoRow>) =>
    onChange(rows.map((r) => (r.key === k ? { ...r, ...patch } : r)));
  return (
    <div className="grid gap-2" data-slot="todo-rows">
      {rows.map((r, n) => (
        <div
          key={r.key}
          className="grid grid-cols-[minmax(0,1fr)_auto] gap-2 rounded-md border border-subtle p-2"
        >
          <div className="grid gap-2">
            <label className="sr-only" htmlFor={`${id}-${r.key}-t`}>
              To-do {n + 1}
            </label>
            <Input
              id={`${id}-${r.key}-t`}
              value={r.title}
              placeholder="What will be done?"
              onChange={(e) => put(r.key, { title: e.target.value })}
            />
            <div className="grid grid-cols-[minmax(0,1fr)_9.5rem] gap-2">
              <label className="sr-only" htmlFor={`${id}-${r.key}-s`}>
                Assignee of to-do {n + 1}
              </label>
              <NativeSelect
                id={`${id}-${r.key}-s`}
                size="sm"
                value={r.seat}
                onChange={(e) => put(r.key, { seat: e.target.value })}
                className="w-full"
              >
                <NativeSelectOption value="">Unassigned</NativeSelectOption>
                {seats.map((s) => (
                  <NativeSelectOption key={s.id} value={s.id}>
                    {s.label}
                  </NativeSelectOption>
                ))}
              </NativeSelect>
              <label className="sr-only" htmlFor={`${id}-${r.key}-d`}>
                Due date of to-do {n + 1}
              </label>
              <Input
                id={`${id}-${r.key}-d`}
                type="date"
                value={r.due}
                onChange={(e) => put(r.key, { due: e.target.value })}
                className="h-8"
              />
            </div>
          </div>
          {rows.length > 1 ? (
            <Button
              type="button"
              variant="ghost"
              size="icon-xs"
              aria-label={`Remove to-do ${n + 1}`}
              onClick={() => onChange(rows.filter((x) => x.key !== r.key))}
            >
              <XIcon aria-hidden="true" />
            </Button>
          ) : null}
        </div>
      ))}
      {rows.length < 10 ? (
        <Button
          type="button"
          variant="ghost"
          size="sm"
          className="justify-self-start"
          onClick={() => onChange([...rows, newTodo()])}
        >
          <PlusIcon data-icon="inline-start" aria-hidden="true" />
          Another to-do
        </Button>
      ) : null}
    </div>
  );
}

let todoKey = 0;
export const newTodo = (title = "", seat = ""): TodoRow => ({
  key: ++todoKey,
  title,
  seat,
  due: defaultDue(),
});
export const todoBody = (r: TodoRow) => ({
  title: r.title.trim(),
  assignee_seat_id: r.seat || null,
  due_at: dueInstant(r.due),
});

type SolveKind = "task" | "decision" | "none";

/** The check date field of a decision, in whole days (1 to 365); undefined: the server's default. */
export function checkDaysOf(v: string): number | undefined {
  const n = Number(v);
  return Number.isInteger(n) && n >= 1 && n <= 365 ? n : undefined;
}

/**
 * RECALL-INBOX-LOOP: what a decision is expected to bring about and when to check (default 14
 * days); on that day its decider gets a "Did it work?" card in the Inbox's Memory.
 */
export function ExpectedFields({
  id,
  expected,
  onExpected,
  days,
  onDays,
}: {
  id: string;
  expected: string;
  onExpected: (v: string) => void;
  days: string;
  onDays: (v: string) => void;
}) {
  return (
    <div className="grid gap-3 sm:grid-cols-[1fr_9rem]" data-slot="decision-check">
      <Field>
        <FieldLabel htmlFor={`${id}-expected`}>Expected result (optional)</FieldLabel>
        <Input
          id={`${id}-expected`}
          maxLength={500}
          value={expected}
          onChange={(e) => onExpected(e.target.value)}
        />
      </Field>
      <Field>
        <FieldLabel htmlFor={`${id}-check`}>Check in (days)</FieldLabel>
        <Input
          id={`${id}-check`}
          inputMode="numeric"
          value={days}
          onChange={(e) => onDays(e.target.value.trim())}
        />
      </Field>
    </div>
  );
}

/** Solve an issue: to-dos, a decision, or just close it; a note either way. */
export function SolveDialog({
  api,
  co,
  issue,
  open,
  onOpenChange,
}: {
  api: Api;
  co: string;
  issue: IssueView;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const qc = useQueryClient();
  const seats = useSeatOptions(api, co);
  const id = useId();
  const [kind, setKind] = useState<SolveKind>("task");
  const [rows, setRows] = useState<TodoRow[]>([]);
  const [decision, setDecision] = useState("");
  const [why, setWhy] = useState("");
  const [expected, setExpected] = useState("");
  const [checkDays, setCheckDays] = useState("14");
  const [note, setNote] = useState("");
  useEffect(() => {
    if (!open) return;
    setKind("task");
    setRows([newTodo("", issue.owner_seat_id ?? "")]);
    setDecision("");
    setWhy("");
    setExpected("");
    setCheckDays("14");
    setNote("");
  }, [open, issue.owner_seat_id]);
  const solve = useMutation({
    mutationFn: () =>
      api.client.issues.resolve({
        params: { co, id: issue.id },
        body: {
          kind,
          ...(kind === "task" ? { tasks: rows.filter((r) => r.title.trim()).map(todoBody) } : {}),
          ...(kind === "decision"
            ? {
                decision: {
                  decision: decision.trim(),
                  why: why.trim() || null,
                  expected_outcome: expected.trim() || null,
                  ...(checkDaysOf(checkDays) ? { check_in_days: checkDaysOf(checkDays) } : {}),
                },
              }
            : {}),
          note: note.trim() || null,
        },
      }),
    onSuccess: (r) => {
      void qc.invalidateQueries({ queryKey: cadenceKeys.root(co) });
      void qc.invalidateQueries({ queryKey: ["company", co, "work"] });
      toast.add({
        title: "Issue solved",
        description:
          kind === "task"
            ? `${r.resolution_refs.length} ${r.resolution_refs.length === 1 ? "to-do" : "to-dos"} created`
            : kind === "decision"
              ? "The decision is logged"
              : issue.title,
        type: "success",
      });
      onOpenChange(false);
    },
  });
  const ready =
    kind === "task"
      ? rows.some((r) => r.title.trim())
      : kind === "decision"
        ? decision.trim().length > 0
        : true;
  return (
    <Dialog open={open} onOpenChange={(o) => !solve.isPending && onOpenChange(o)}>
      <DialogContent className="sm:max-w-[600px]">
        <form
          aria-label="Solve issue"
          className="grid gap-5"
          onSubmit={(e) => {
            e.preventDefault();
            if (ready) solve.mutate();
          }}
        >
          <DialogHeader>
            <DialogTitle>Solve “{issue.title}”</DialogTitle>
            <DialogDescription>
              An issue is solved by to-dos (due in 7 days unless you say otherwise), by a decision
              for the log, or simply closed.
            </DialogDescription>
          </DialogHeader>
          <Segmented
            aria-label="Solved by"
            value={kind}
            onValueChange={setKind}
            options={[
              { value: "task", label: "To-dos" },
              { value: "decision", label: "Decision" },
              { value: "none", label: "Just close" },
            ]}
          />
          {kind === "task" ? <TodoRows rows={rows} seats={seats} onChange={setRows} /> : null}
          {kind === "decision" ? (
            <>
              <Field>
                <FieldLabel htmlFor={`${id}-decision`}>Decision</FieldLabel>
                <Textarea
                  id={`${id}-decision`}
                  rows={2}
                  value={decision}
                  onChange={(e) => setDecision(e.target.value)}
                />
              </Field>
              <Field>
                <FieldLabel htmlFor={`${id}-why`}>Why (optional)</FieldLabel>
                <Textarea
                  id={`${id}-why`}
                  rows={2}
                  value={why}
                  onChange={(e) => setWhy(e.target.value)}
                />
              </Field>
              <ExpectedFields
                id={id}
                expected={expected}
                onExpected={setExpected}
                days={checkDays}
                onDays={setCheckDays}
              />
            </>
          ) : null}
          <Field>
            <FieldLabel htmlFor={`${id}-note`}>Note (optional)</FieldLabel>
            <Input id={`${id}-note`} value={note} onChange={(e) => setNote(e.target.value)} />
          </Field>
          {solve.isError ? (
            <p role="alert" className="text-sm text-danger-text">
              {problemText(solve.error, "The issue could not be solved.")}
            </p>
          ) : null}
          <DialogFooter>
            <DialogClose render={<Button type="button" variant="outline" />}>Cancel</DialogClose>
            <Button type="submit" disabled={solve.isPending || !ready}>
              Solve issue
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

/**
 * The visible "Raise issue" action of another screen (a chat message, a task, a missed KPI, an
 * off-track priority): an icon button with its tooltip, and the raise dialog prefilled.
 */
export function RaiseIssueButton({
  api,
  co,
  defaults,
  size = "icon-xs",
  className,
  label = "Raise issue",
  children,
}: {
  api: Api;
  co: string;
  defaults: IssueDefaults;
  size?: "icon-xs" | "icon-sm";
  className?: string;
  label?: string;
  children?: ReactNode;
}) {
  const [open, setOpen] = useState(false);
  return (
    <>
      <Tooltip>
        <TooltipTrigger
          render={
            <Button
              variant="ghost"
              size={size}
              aria-label={label}
              className={className}
              onClick={(e) => {
                e.stopPropagation();
                setOpen(true);
              }}
            />
          }
        >
          {children ?? <CircleAlertIcon aria-hidden="true" />}
        </TooltipTrigger>
        <TooltipContent>{label}</TooltipContent>
      </Tooltip>
      {open ? (
        <IssueDialog api={api} co={co} open={open} onOpenChange={setOpen} defaults={defaults} />
      ) : null}
    </>
  );
}
